import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mail, ArrowRight, Loader2, Check, Sparkles } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Lightweight lead-magnet pinned to the AI Workflow Simulator.
 * Captures an email and posts to the existing /api/contact endpoint with a
 * preset service interest + message derived from the workflow the visitor ran.
 */
export default function RoadmapCapture({ label, service }) {
    const [email, setEmail] = useState("");
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);
    const [sent, setSent] = useState(false);

    const onSubmit = async (e) => {
        e.preventDefault();
        if (!EMAIL_RE.test(email)) {
            setError("Enter a valid work email.");
            return;
        }
        setError("");
        setLoading(true);
        try {
            await axios.post(`${API}/contact`, {
                name: "AI roadmap request",
                email,
                service_interest: service,
                message: `Requested an AI roadmap after running the "${label}" workflow simulation on the website. Please send the relevant ${label} AI roadmap and follow up.`,
            });
            setSent(true);
            setEmail("");
            toast.success("On its way — we'll send your AI roadmap within one business day.");
        } catch (err) {
            const status = err?.response?.status;
            if (status === 429) {
                toast.error("Too many requests from this address. Please try again later.");
            } else {
                toast.error("Couldn't send that. Please try again.");
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <div
            className="mt-5 rounded-2xl pqx-glass-dark p-5 md:p-6 flex flex-col md:flex-row md:items-center gap-5 md:gap-8"
            data-testid="roadmap-capture"
        >
            <div className="flex items-start gap-3.5 flex-1">
                <span className="w-10 h-10 grid place-items-center rounded-xl bg-[#FF4040]/15 text-[#FF4040] shrink-0">
                    <Sparkles className="w-5 h-5" />
                </span>
                <div>
                    <p className="text-[15px] font-semibold text-white leading-snug">
                        Want this as a plan? Get the <span className="text-[#FF4040]">{label}</span> AI roadmap.
                    </p>
                    <p className="mt-1 text-[13px] text-white/60 leading-relaxed">
                        We'll email a sequenced, ROI-mapped outline for your workflow — no sales call required.
                    </p>
                </div>
            </div>

            {sent ? (
                <div
                    className="flex items-center gap-2.5 rounded-xl bg-[#FF4040]/12 border border-[#FF4040]/25 px-4 py-3 text-white shrink-0"
                    data-testid="roadmap-success"
                >
                    <Check className="w-4 h-4 text-[#FF4040]" />
                    <p className="text-[13.5px] font-medium">Sent — check your inbox shortly.</p>
                </div>
            ) : (
                <form
                    onSubmit={onSubmit}
                    className="w-full md:w-auto shrink-0"
                    noValidate
                    data-testid="roadmap-form"
                >
                    <div className="flex flex-col sm:flex-row gap-2.5">
                        <div className="relative">
                            <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
                            <input
                                type="email"
                                value={email}
                                onChange={(e) => {
                                    setEmail(e.target.value);
                                    setError("");
                                }}
                                placeholder="you@company.com"
                                data-testid="roadmap-email-input"
                                aria-label="Work email"
                                className="pqx-input-dark w-full sm:w-64 h-11 rounded-full bg-[#121212]/85 border border-white/20 pl-10 pr-4 text-[14px] text-white placeholder:text-white/45 focus:outline-none focus:border-[#FF4040]/60 focus:bg-[#121212] transition-colors"
                            />
                        </div>
                        <button
                            type="submit"
                            disabled={loading}
                            data-testid="roadmap-submit"
                            className="pqx-btn-accent !px-5 whitespace-nowrap disabled:opacity-70 disabled:cursor-not-allowed"
                        >
                            {loading ? (
                                <>
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                    Sending…
                                </>
                            ) : (
                                <>
                                    Email me the roadmap
                                    <ArrowRight className="w-4 h-4" />
                                </>
                            )}
                        </button>
                    </div>
                    {error && (
                        <p className="mt-2 text-[12px] text-[#FF4040]" data-testid="roadmap-error">
                            {error}
                        </p>
                    )}
                </form>
            )}
        </div>
    );
}
